import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2, ShieldAlert, Unlock, ChevronDown, ChevronRight } from 'lucide-react';
import AdminReferralBannerEditor from './AdminReferralBannerEditor';

const API = process.env.REACT_APP_BACKEND_URL || '';
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

function AdminReferralLinksList() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [page, setPage] = useState(0);
  const [expandedSlug, setExpandedSlug] = useState(null);
  const [flaggedDetails, setFlaggedDetails] = useState({}); // { [slug]: { loading, events } }
  const [unblocking, setUnblocking] = useState(null); // `${slug}|${userId}`
  const pageSize = 50;

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/api/admin/referrals`, {
      params: { skip: page * pageSize, limit: pageSize },
      headers: getAuthHeader(),
    })
      .then(r => {
        setItems(r.data?.items || []);
        setTotal(r.data?.total || 0);
      })
      .catch(() => toast.error('Не удалось загрузить список ссылок'))
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const onDelete = async (slug) => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm(`Удалить ссылку ${slug}? Действие необратимо.`)) return;
    setDeleting(slug);
    try {
      await axios.delete(`${API}/api/admin/referrals/${slug}`, { headers: getAuthHeader() });
      toast.success('Ссылка удалена');
      setItems(arr => arr.filter(x => x.id !== slug));
      setTotal(t => Math.max(0, t - 1));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ошибка удаления');
    } finally {
      setDeleting(null);
    }
  };

  const loadFlaggedDetails = useCallback(async (slug) => {
    setFlaggedDetails(prev => ({ ...prev, [slug]: { loading: true, events: [] } }));
    try {
      const r = await axios.get(`${API}/api/admin/referrals/${slug}/flagged-events`, { headers: getAuthHeader() });
      setFlaggedDetails(prev => ({ ...prev, [slug]: { loading: false, events: r.data?.events || [] } }));
    } catch (e) {
      toast.error('Не удалось загрузить подозрительные приглашения');
      setFlaggedDetails(prev => ({ ...prev, [slug]: { loading: false, events: [] } }));
    }
  }, []);

  const toggleExpand = (slug) => {
    if (expandedSlug === slug) {
      setExpandedSlug(null);
      return;
    }
    setExpandedSlug(slug);
    if (!flaggedDetails[slug]) loadFlaggedDetails(slug);
  };

  const onUnblock = async (slug, newUserId) => {
    setUnblocking(`${slug}|${newUserId}`);
    try {
      const r = await axios.post(
        `${API}/api/admin/referrals/${slug}/events/${newUserId}/unblock`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(`Разблокировано. Начислено отправителю: ${r.data?.awarded || 0}`);
      // refresh: drop the unblocked event and decrement flagged_count
      setFlaggedDetails(prev => ({
        ...prev,
        [slug]: {
          ...(prev[slug] || {}),
          events: (prev[slug]?.events || []).filter(e => e.user_id !== newUserId),
        },
      }));
      setItems(arr => arr.map(x => x.id === slug
        ? { ...x, flagged_count: Math.max(0, (x.flagged_count || 0) - 1),
            sender_points_earned: (x.sender_points_earned || 0) + (r.data?.awarded || 0) }
        : x));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ошибка разблокировки');
    } finally {
      setUnblocking(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div data-testid="admin-referral-links" style={{ marginTop: '2rem' }}>
      <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>
        Все реферальные ссылки {total ? `(${total})` : ''}
      </h3>
      <p style={{ fontSize: 13, color: '#666', marginBottom: '0.75rem' }}>
        Удаление ссылки безвозвратно. Связанные клики/регистрации сохраняются в
        логах баллов, но ссылка перестанет существовать.
      </p>

      {loading ? (
        <div style={{ padding: '0.75rem', color: '#666' }}>Загрузка…</div>
      ) : items.length === 0 ? (
        <div style={{ padding: '0.75rem', color: '#666', background: '#f9fafb', borderRadius: 8 }}>
          Реферальных ссылок ещё нет.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map(l => {
            const isExpanded = expandedSlug === l.id;
            const flagged = l.flagged_count || 0;
            const details = flaggedDetails[l.id];
            return (
            <div
              key={l.id}
              data-testid={`admin-ref-row-${l.id}`}
              style={{
                border: '1px solid ' + (flagged > 0 ? '#fecaca' : '#e5e7eb'),
                borderRadius: 10,
                background: flagged > 0 ? '#fffbfb' : 'white',
                fontSize: 13,
              }}
            >
              <div style={{
                display: 'grid', gridTemplateColumns: '1.4fr 1.6fr 0.9fr auto',
                alignItems: 'center', gap: 12,
                padding: '10px 12px',
              }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#0a4797', wordBreak: 'break-all' }}>
                  /ref/{l.id}
                </div>
                <div style={{ fontSize: 11, color: '#6b7280' }}>
                  {l.created_at ? new Date(l.created_at).toLocaleString('ru') : ''}
                </div>
                {flagged > 0 && (
                  <button
                    onClick={() => toggleExpand(l.id)}
                    data-testid={`admin-ref-toggle-flagged-${l.id}`}
                    style={{
                      marginTop: 6, display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '3px 8px', borderRadius: 999, border: '1px solid #fecaca',
                      background: '#fef2f2', color: '#b91c1c', fontWeight: 700, fontSize: 11,
                      cursor: 'pointer',
                    }}
                    title="Развернуть список подозрительных приглашений"
                  >
                    <ShieldAlert size={12} />
                    Подозрительных: {flagged}
                    {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  </button>
                )}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: '#1f2937' }}>
                  {l.owner_name || '—'}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', wordBreak: 'break-all' }}>
                  {l.owner_email || ''}
                </div>
              </div>
              <div style={{ fontSize: 12, color: '#374151' }}>
                <div>👀 кликов: <b>{l.click_count || 0}</b></div>
                <div>👤 регистраций: <b>{l.registrations_count || 0}</b></div>
                <div>🏆 баллов: <b>{l.sender_points_earned || 0}</b></div>
                {l.via_clicks && Object.values(l.via_clicks).some(n => n > 0) && (
                  <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {Object.entries(l.via_clicks).filter(([, n]) => n > 0).map(([ch, n]) => (
                      <span
                        key={ch}
                        title={`Клики через ${ch.toUpperCase()}`}
                        style={{
                          fontSize: 10.5, padding: '1px 6px', borderRadius: 999,
                          background: '#eef2ff', color: '#312e81', border: '1px solid #c7d2fe',
                          fontWeight: 600,
                        }}
                      >
                        {ch.toUpperCase()}: {n}
                      </span>
                    ))}
                  </div>
                )}
                {Array.isArray(l.shared_via) && l.shared_via.length > 0 && (
                  <div style={{ marginTop: 3, fontSize: 10.5, color: '#6b7280' }}>
                    Отправлено: {l.shared_via.join(', ').toUpperCase()}
                  </div>
                )}
              </div>
              <button
                onClick={() => onDelete(l.id)}
                disabled={deleting === l.id}
                data-testid={`admin-ref-del-${l.id}`}
                title="Удалить ссылку"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 12px', border: '1px solid #fecaca',
                  background: deleting === l.id ? '#fef2f2' : 'white', color: '#b91c1c',
                  borderRadius: 8, cursor: deleting === l.id ? 'wait' : 'pointer', fontSize: 12, fontWeight: 600,
                }}
              >
                <Trash2 size={14} />
                {deleting === l.id ? 'Удаляем…' : 'Удалить'}
              </button>
              </div>
              {isExpanded && flagged > 0 && (
                <div
                  data-testid={`admin-ref-flagged-panel-${l.id}`}
                  style={{
                    borderTop: '1px dashed #fecaca',
                    padding: '10px 12px',
                    background: '#fff7f7',
                  }}
                >
                  <div style={{ fontSize: 12, color: '#7f1d1d', fontWeight: 700, marginBottom: 6 }}>
                    Подозрительные приглашения
                  </div>
                  {details?.loading ? (
                    <div style={{ fontSize: 12, color: '#6b7280' }}>Загрузка…</div>
                  ) : (details?.events?.length || 0) === 0 ? (
                    <div style={{ fontSize: 12, color: '#6b7280' }}>Нет неразблокированных приглашений.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {details.events.map(ev => {
                        const key = `${l.id}|${ev.user_id}`;
                        const busy = unblocking === key;
                        return (
                          <div
                            key={ev.user_id}
                            data-testid={`admin-ref-flagged-event-${ev.user_id}`}
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '1.4fr 1.4fr auto',
                              gap: 10, alignItems: 'center',
                              padding: '8px 10px', background: 'white',
                              border: '1px solid #fee2e2', borderRadius: 8,
                            }}
                          >
                            <div>
                              <div style={{ fontWeight: 600, color: '#111827', fontSize: 12 }}>
                                {ev.new_user_full_name || ev.new_user_email || '—'}
                              </div>
                              <div style={{ fontSize: 11, color: '#6b7280' }}>
                                {ev.new_user_email}
                              </div>
                              <div style={{ fontSize: 10.5, color: '#6b7280', marginTop: 2 }}>
                                {ev.ts ? new Date(ev.ts).toLocaleString('ru') : ''}
                              </div>
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                              {(ev.flagged_reasons || []).map(r => (
                                <span key={r} style={{
                                  fontSize: 10.5, padding: '2px 7px', borderRadius: 999,
                                  background: '#fef2f2', color: '#b91c1c',
                                  border: '1px solid #fecaca', fontWeight: 600,
                                }}>{r}</span>
                              ))}
                            </div>
                            <button
                              onClick={() => onUnblock(l.id, ev.user_id)}
                              disabled={busy}
                              data-testid={`admin-ref-unblock-${ev.user_id}`}
                              title="Снять флаг и начислить отправителю бонус ретроактивно"
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 6,
                                padding: '6px 10px', border: '1px solid #bbf7d0',
                                background: busy ? '#f0fdf4' : '#ecfdf5', color: '#166534',
                                borderRadius: 8, cursor: busy ? 'wait' : 'pointer',
                                fontSize: 12, fontWeight: 600,
                              }}
                            >
                              <Unlock size={12} />
                              {busy ? 'Разблокируем…' : 'Разблокировать вручную'}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #d1d5db', background: 'white', cursor: page === 0 ? 'default' : 'pointer' }}
          >
            ←
          </button>
          <span style={{ fontSize: 13, color: '#666' }}>
            Стр. {page + 1} из {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #d1d5db', background: 'white', cursor: page >= totalPages - 1 ? 'default' : 'pointer' }}
          >
            →
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Admin panel section: virality / referral config + all-links management.
 * Rendered inside AdminPanel.js when activeTab === 'virality'.
 */
export default function AdminViralityConfig() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/admin/virality-config`, { headers: getAuthHeader() })
      .then(r => setCfg(r.data))
      .catch(() => toast.error('Не удалось загрузить настройки вирусности'))
      .finally(() => setLoading(false));
  }, []);

  const update = (key, val) => setCfg(c => ({ ...c, [key]: Number(val) }));

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/virality-config`, cfg, { headers: getAuthHeader() });
      toast.success('Настройки сохранены');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: '1rem' }}>Загрузка…</div>;
  if (!cfg) return null;

  const fields = [
    { key: 'link_click_points', label: 'Баллы за первый переход по ссылке' },
    { key: 'sender_reg_points', label: 'Баллы отправителю за регистрацию приглашённого' },
    { key: 'new_player_welcome_points', label: 'Приветственные баллы новому игроку' },
    { key: 'hint_coordinates_cost', label: 'Стоимость подсказки координат' },
    { key: 'hint_objects_cost', label: 'Стоимость подсказки объекта' },
    { key: 'max_registrations_per_link', label: 'Макс. регистраций по одной ссылке (0 = без лимита)' },
  ];

  return (
    <div data-testid="admin-virality-config" style={{ maxWidth: 980, padding: '0.5rem 0' }}>
      <p style={{ fontSize: 13, color: '#666', marginBottom: '1.25rem' }}>
        Управляйте баллами за приглашения и стоимостью подсказок. Изменения применяются сразу.
      </p>
      <div style={{ display: 'grid', gap: '0.85rem' }}>
        {fields.map(f => (
          <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <label style={{ flex: '1 1 320px', fontSize: 14, color: '#374151' }}>{f.label}</label>
            <input
              type="number"
              min={0}
              value={cfg[f.key] ?? 0}
              onChange={e => update(f.key, e.target.value)}
              data-testid={`vir-cfg-${f.key}`}
              style={{
                width: 120, padding: '8px 10px', border: '1px solid #d1d5db',
                borderRadius: 8, fontSize: 14, textAlign: 'right',
              }}
            />
          </div>
        ))}
      </div>
      <button
        onClick={save}
        disabled={saving}
        data-testid="vir-cfg-save"
        style={{
          marginTop: '1.25rem', padding: '10px 22px',
          background: saving ? '#9ca3af' : '#0a4797', color: 'white',
          border: 'none', borderRadius: 8, fontWeight: 600, cursor: saving ? 'wait' : 'pointer',
        }}
      >
        {saving ? 'Сохраняем…' : 'Сохранить настройки'}
      </button>

      <AdminReferralBannerEditor />
      <AdminReferralLinksList />
    </div>
  );
}
